import { useRouter } from 'next/router';

import { useCallback, useEffect, useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';

import { startOfDay } from 'date-fns/startOfDay';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { urlMap } from '@nocowanie/core/consts';
import { dateHelpers } from '@nocowanie/core/helpers';
import { SearchEngineContainer } from '@nocowanie/common-ui/components/organisms/search-engine-container';
import { useSearchCookie } from '@nocowanie/common-ui/hooks';
import { SearchEngineModel } from '@nocowanie/common-ui/models';

import { environment } from '@app/environments/environment';
import { useDefaultSearchFormData } from '@app/hooks/useDefaultSearchFormData';
import { useSaveFiltersToCrossStorage } from '@app/hooks/useSaveFiltersToCrossStorage';
import { useSearchEngineDataLayerEvents } from '@app/hooks/useSearchEngineDataLayerEvents';
import * as accommodationSlice from '@app/state/slices/accommodation.slice';
import * as commonUiStateSlice from '@app/state/slices/common-ui-state.slice';
import { useAppDispatch, useAppSelector } from '@app/state/state';

import { SearchEngineWrapperProps } from './search-engine-wrapper.props';

import { useAutocompleteData } from '../autocomplete-wrapper/use-autocomplete-data.hook';

export const SearchEngineWrapper = ({
    isMobileCompactVersion = true,
    isV7SearchEngineVersion,
    defaultBaseUrl = urlMap.search,
    initialSearchData,
    ...rest
}: SearchEngineWrapperProps) => {
    const { pathname } = useRouter();
    const isHomePage = pathname === '/';
    const mobileDrawerDisclosure = useDisclosure();
    const { setCookie } = useSearchCookie(environment.COOKIE_DOMAIN);
    const dispatch = useAppDispatch();
    const filters = useAppSelector(accommodationSlice.selectors.selectFilters);
    const [prevFilters, setPrevFilters] = useState({ ...filters });
    const accommodation = useAppSelector(accommodationSlice.selectors.selectCurrentAccommodation);
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const defaultSearchFormData = useDefaultSearchFormData(initialSearchData);
    const [accommodationLocation, setAccommodationLocation] = useState(
        accommodation?.settlement?.name || '',
    );
    const [location, setLocation] = useState(accommodationLocation);
    const [accommodationId, setAccommodationId] = useState(accommodation?.id);
    const [instantReservation, setinstantReservation] = useState<boolean | undefined>(undefined);
    const [isInitialSearchFormDataSet, setIsInitialSearchFormDataSet] = useState(false);
    const [initialSearchFormData, setInitialSearchFormData] = useState(defaultSearchFormData);
    const [baseUrl, setBaseUrl] = useState<string | null>(null);
    const autocompleteData = useAutocompleteData({
        setBaseUrl,
        onAccommodationSelected: useCallback(() => {
            mobileDrawerDisclosure.onClose();
        }, [mobileDrawerDisclosure]),
        initialQuery: location,
    });

    const { sendSearchEngineDataLayerEvent } = useSearchEngineDataLayerEvents();

    useEffect(() => {
        if (!isHomePage) return;

        setInitialSearchFormData(defaultSearchFormData);
    }, [defaultSearchFormData, isHomePage]);

    useEffect(() => {
        if (
            !accommodation?.settlement?.name ||
            (accommodationLocation === accommodation?.settlement?.name &&
                accommodationId === accommodation?.id)
        ) {
            return;
        }

        setAccommodationLocation(accommodation?.settlement?.name);
        setAccommodationId(accommodation?.id);
        setIsInitialSearchFormDataSet(false);
    }, [
        accommodation?.id,
        accommodation?.settlement?.name,
        accommodationId,
        accommodationLocation,
    ]);

    useEffect(() => {
        if (accommodationLocation && !isInitialSearchFormDataSet) {
            setTimeout(() => {
                setIsInitialSearchFormDataSet(true);
                setLocation(accommodationLocation);
                autocompleteData.setQuery(accommodationLocation);
            }, 0);
        }
    }, [accommodationLocation, autocompleteData, isInitialSearchFormDataSet]);

    useEffect(() => {
        let newCheckIn, newCheckOut;

        if (
            (prevFilters.checkIn && !filters?.checkIn) ||
            (prevFilters.checkOut && !filters?.checkOut)
        ) {
            // Filters (dates) can be set to null
            newCheckIn = null;
            newCheckOut = null;
        } else {
            newCheckIn = filters?.checkIn
                ? startOfDay(new Date(filters.checkIn))
                : initialSearchFormData.dates.checkInDate;
            newCheckOut = filters?.checkOut
                ? startOfDay(new Date(filters.checkOut))
                : initialSearchFormData.dates.checkOutDate;
        }

        const newFormData = {
            location: location || initialSearchFormData.location || '',
            dates: {
                checkInDate: newCheckIn,
                checkOutDate: newCheckOut,
            },
            guests: {
                adultsCount:
                    typeof filters?.guestsCount === 'number'
                        ? filters.guestsCount
                        : initialSearchFormData.guests.adultsCount,
                childrenCount:
                    typeof filters?.childrenCount === 'number'
                        ? filters.childrenCount
                        : initialSearchFormData.guests.childrenCount,
                childrenAges: filters?.childrenAges
                    ? filters.childrenAges.filter((age): age is number => age !== null)
                    : initialSearchFormData.guests.childrenAges,
            },
            instantReservation: !isNil(instantReservation)
                ? instantReservation
                : initialSearchFormData.instantReservation,
            flexDates:
                typeof filters?.flexDates === 'number'
                    ? filters.flexDates
                    : initialSearchFormData.flexDates,
        };

        setPrevFilters({ ...filters });
        setCookie(newFormData);

        if (!isEqual(newFormData, initialSearchFormData)) {
            setInitialSearchFormData({ ...newFormData });
        }
        // prevFilters - ommited from dependencies on purpose
    }, [
        filters,
        initialSearchFormData,
        instantReservation,
        isInitialSearchFormDataSet,
        location /*, prevFilters*/,
    ]);

    const onSearchEngineDataChanged = (data: SearchEngineModel) => {
        if (isEqual(initialSearchFormData, data)) {
            return;
        }

        const currenFilters = {
            checkIn: initialSearchFormData.dates.checkInDate,
            checkOut: initialSearchFormData.dates.checkOutDate,
            guestsCount: initialSearchFormData.guests.adultsCount,
            childrenCount: initialSearchFormData.guests.childrenCount,
            childrenAges: initialSearchFormData.guests.childrenAges,
            flexDates: initialSearchFormData.flexDates,
        };

        const { dates, guests, flexDates } = data;
        const newFilters = {
            checkIn: dates.checkInDate,
            checkOut: dates.checkOutDate,
            guestsCount: guests.adultsCount,
            childrenCount: guests.childrenCount,
            childrenAges: guests.childrenAges,
            flexDates,
        };

        if (location !== data.location) {
            setLocation(data.location);
        }

        if (!isNil(data.instantReservation) && instantReservation !== data.instantReservation) {
            setinstantReservation(data.instantReservation);
        }

        if (!isEqual(currenFilters, newFilters)) {
            dispatch(
                accommodationSlice.actions.updateFilters({
                    checkIn: dates.checkInDate
                        ? dateHelpers.format(dates.checkInDate, environment.API_DATE_FORMAT)
                        : null,
                    checkOut: dates.checkOutDate
                        ? dateHelpers.format(dates.checkOutDate, environment.API_DATE_FORMAT)
                        : null,
                    guestsCount: guests.adultsCount,
                    childrenCount: guests.childrenCount,
                    childrenAges: guests.childrenAges,
                    flexDates,
                }),
            );
        }

        setCookie(data);
    };

    const saveFiltersToCrossStorage = useSaveFiltersToCrossStorage();

    const onSearchEngineSubmit = useCallback(
        ({ searchEngineData }: { searchEngineData: SearchEngineModel }) => {
            sendSearchEngineDataLayerEvent(searchEngineData);

            saveFiltersToCrossStorage({
                checkIn: searchEngineData.dates.checkInDate
                    ? dateHelpers.format(
                          searchEngineData.dates.checkInDate,
                          environment.API_DATE_FORMAT,
                      )
                    : null,
                checkOut: searchEngineData.dates.checkOutDate
                    ? dateHelpers.format(
                          searchEngineData.dates.checkOutDate,
                          environment.API_DATE_FORMAT,
                      )
                    : null,
                guestsCount: searchEngineData.guests.adultsCount,
                childrenCount: searchEngineData.guests.childrenCount,
                childrenAges: searchEngineData.guests.childrenAges,
                instantReservation: searchEngineData.instantReservation,
                flexDates: searchEngineData.flexDates,
            });

            setCookie(searchEngineData);
        },
        [saveFiltersToCrossStorage, sendSearchEngineDataLayerEvent, setCookie],
    );

    return (
        <SearchEngineContainer
            initialFormData={initialSearchFormData}
            onSearchEngineDataChanged={onSearchEngineDataChanged}
            onSearchEngineSubmit={onSearchEngineSubmit}
            isV7SearchEngineVersion={isV7SearchEngineVersion}
            isMobileCompactVersion={isMobileCompactVersion}
            isMobileBrowser={isMobileBrowser}
            autocompleteData={autocompleteData}
            baseUrl={baseUrl || defaultBaseUrl}
            mobileDrawerDisclosure={mobileDrawerDisclosure}
            {...rest}
        />
    );
};
